import React, { useState } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const VehicleConditionAssessment = ({ 
  formData, 
  onFormChange, 
  errors = {} 
}) => {
  const overallConditionOptions = [
    { value: '', label: 'Select Overall Condition' },
    { value: 'excellent', label: 'Excellent - Like new, no visible wear' },
    { value: 'very-good', label: 'Very Good - Minor wear, well maintained' },
    { value: 'good', label: 'Good - Normal wear, some minor issues' },
    { value: 'fair', label: 'Fair - Noticeable wear, needs some work' },
    { value: 'poor', label: 'Poor - Significant issues, major repairs needed' }
  ];

  const damageTypes = [
    { id: 'scratches', label: 'Scratches', icon: 'Zap' },
    { id: 'dents', label: 'Dents', icon: 'Circle' },
    { id: 'rust', label: 'Rust', icon: 'AlertTriangle' },
    { id: 'paint-damage', label: 'Paint Damage', icon: 'Palette' },
    { id: 'glass-damage', label: 'Glass Damage', icon: 'Square' },
    { id: 'interior-wear', label: 'Interior Wear', icon: 'Home' }
  ];

  const mechanicalIssues = [
    { id: 'engine', label: 'Engine Issues' },
    { id: 'transmission', label: 'Transmission Problems' },
    { id: 'brakes', label: 'Brake Issues' },
    { id: 'suspension', label: 'Suspension Problems' },
    { id: 'electrical', label: 'Electrical Issues' },
    { id: 'ac-heating', label: 'AC/Heating Problems' },
    { id: 'exhaust', label: 'Exhaust Issues' },
    { id: 'tires', label: 'Tire Replacement Needed' }
  ];

  const vehicleAreas = [
    { id: 'front-bumper', label: 'Front Bumper', x: 50, y: 20 },
    { id: 'hood', label: 'Hood', x: 50, y: 35 },
    { id: 'windshield', label: 'Windshield', x: 50, y: 45 },
    { id: 'roof', label: 'Roof', x: 50, y: 55 },
    { id: 'rear-window', label: 'Rear Window', x: 50, y: 65 },
    { id: 'trunk', label: 'Trunk', x: 50, y: 75 },
    { id: 'rear-bumper', label: 'Rear Bumper', x: 50, y: 85 },
    { id: 'driver-door', label: 'Driver Door', x: 25, y: 50 },
    { id: 'passenger-door', label: 'Passenger Door', x: 75, y: 50 },
    { id: 'driver-quarter', label: 'Driver Quarter Panel', x: 25, y: 70 },
    { id: 'passenger-quarter', label: 'Passenger Quarter Panel', x: 75, y: 70 }
  ];

  // New options for the requested fields
  const serviceBookOptions = [
    { value: '', label: 'Select status' },
    { value: 'full', label: 'Complete Service Book' },
    { value: 'incomplete', label: 'Incomplete Service Book' }
  ];

  const platesDepositedOptions = [
    { value: '', label: 'Select status' },
    { value: 'no', label: 'No' },
    { value: 'smallEngine', label: 'Yes (≤1900cc)' },
    { value: 'largeEngine', label: 'Yes (>1900cc)' }
  ];

  const handleDamageToggle = (damageType) => {
    const currentDamage = formData.damageTypes || [];
    const updatedDamage = currentDamage.includes(damageType)
      ? currentDamage.filter(type => type !== damageType)
      : [...currentDamage, damageType];
    
    onFormChange('damageTypes', updatedDamage);
  };

  const handleMechanicalIssueToggle = (issue) => {
    const currentIssues = formData.mechanicalIssues || [];
    const updatedIssues = currentIssues.includes(issue)
      ? currentIssues.filter(i => i !== issue)
      : [...currentIssues, issue];
    
    onFormChange('mechanicalIssues', updatedIssues);
  };

  const handleAreaClick = (area) => {
    const currentAreas = formData.damageAreas || [];
    const isAlreadySelected = currentAreas.some(a => a.id === area.id);
    const updatedAreas = isAlreadySelected
      ? currentAreas.filter(a => a.id !== area.id)
      : [...currentAreas, { id: area.id, label: area.label }];
    
    onFormChange('damageAreas', updatedAreas);
  };

  const isDamageAreaSelected = (areaId) => {
    return (formData.damageAreas || []).some(area => area.id === areaId);
  };

  return (
    <div className="space-y-6">
      {/* Overall Condition */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Overall Condition</h3>
        <Select
          label="Overall Vehicle Condition"
          options={overallConditionOptions}
          value={formData.overallCondition || ''}
          onChange={(value) => onFormChange('overallCondition', value)}
          error={errors.overallCondition}
          required
          description="Select the condition that best describes your vehicle"
        />
      </div>

      {/* Damage Types */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Damage Assessment</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Select all types of damage present on your vehicle
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {damageTypes.map((damage) => (
            <div
              key={damage.id}
              onClick={() => handleDamageToggle(damage.id)}
              className={`
                flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-smooth
                ${(formData.damageTypes || []).includes(damage.id)
                  ? 'border-primary bg-primary/5 text-primary' :'border-border hover:border-primary/50 hover:bg-muted/50'
                }
              `}
            >
              <Icon name={damage.icon} size={20} />
              <span className="text-sm font-medium">{damage.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Interactive Vehicle Diagram */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Damage Location</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Tap on areas of the vehicle where damage is present
        </p>
        <div className="relative bg-muted/30 rounded-lg p-8 mx-auto max-w-md">
          <svg
            viewBox="0 0 100 100"
            className="w-full h-64 border border-border rounded-lg bg-background"
          >
            <rect x="20" y="15" width="60" height="70" rx="8" fill="currentColor" className="text-muted" stroke="currentColor" strokeWidth="1"/>
            
            {vehicleAreas.map((area) => (
              <circle
                key={area.id}
                cx={area.x}
                cy={area.y}
                r="4"
                className={`cursor-pointer transition-smooth ${
                  isDamageAreaSelected(area.id)
                    ? 'fill-destructive stroke-destructive' :'fill-primary/20 stroke-primary hover:fill-primary/40'
                }`}
                strokeWidth="2"
                onClick={() => handleAreaClick(area)}
              />
            ))}
          </svg>
        </div>
        {(formData.damageAreas || []).length > 0 && (
          <div className="mt-4 p-4 bg-muted/50 rounded-lg">
            <h4 className="text-sm font-medium text-foreground mb-2">Selected Damage Areas:</h4>
            <div className="flex flex-wrap gap-2">
              {(formData.damageAreas || []).map((area) => (
                <span
                  key={area.id}
                  className="inline-flex items-center px-2 py-1 bg-destructive/10 text-destructive text-xs rounded-md"
                >
                  {area.label}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Mechanical Issues */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Mechanical Issues</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Check any mechanical problems your vehicle currently has
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {mechanicalIssues.map((issue) => (
            <Checkbox
              key={issue.id}
              label={issue.label}
              checked={(formData.mechanicalIssues || []).includes(issue.id)}
              onChange={() => handleMechanicalIssueToggle(issue.id)}
            />
          ))}
        </div>
      </div>
      
      {/* New: Additional Details section for the requested fields */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Additional Details</h3>
        
        {/* Checkbox fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          <Checkbox
            label="1st owner"
            checked={formData.isFirstOwner}
            onChange={() => onFormChange('isFirstOwner', !formData.isFirstOwner)}
          />
          <Checkbox
            label="Accident"
            checked={formData.hasAccident}
            onChange={() => onFormChange('hasAccident', !formData.hasAccident)}
          />
          <Checkbox
            label="Needs Repairs"
            checked={formData.needsRepairs}
            onChange={() => onFormChange('needsRepairs', !formData.needsRepairs)}
          />
          <Checkbox
            label="Needs Tires / Service"
            checked={formData.needsTiresService}
            onChange={() => onFormChange('needsTiresService', !formData.needsTiresService)}
          />
          <Checkbox
            label="MOT (KTEO) is missing"
            checked={formData.missingKteo}
            onChange={() => onFormChange('missingKteo', !formData.missingKteo)}
          />
        </div>

        {/* Input fields */}
        <div className="space-y-4 mb-6">
          <Input
            label="Number of Owners"
            type="number"
            min="1"
            value={formData.numberOfOwners || 1}
            onChange={(e) => onFormChange('numberOfOwners', parseInt(e.target.value, 10))}
            // description="More than 2 owners reduces value by 3%"
          />
          <Input
            label="Number of Keys"
            type="number"
            min="0"
            value={formData.numberOfKeys || 2}
            onChange={(e) => onFormChange('numberOfKeys', parseInt(e.target.value, 10))}
            // description="Less than 2 keys reduces value by €500"
          />
          <Input
            label="Additional Equipment"
            type="text"
            value={formData.additionalEquipment || ''}
            onChange={(e) => onFormChange('additionalEquipment', e.target.value)}
            // description="Enter details about custom accessories or upgrades"
          />
        </div>
        
        {/* Select fields */}
        <div className="space-y-4">
          <Select
            label="Service Book Status"
            options={serviceBookOptions}
            value={formData.serviceBook || ''}
            onChange={(value) => onFormChange('serviceBook', value)}
            // description="Complete service book adds 5%, Incomplete reduces by 2%"
          />
          <Select
            label="License plates deposited?"
            options={platesDepositedOptions}
            value={formData.platesDeposited || ''}
            onChange={(value) => onFormChange('platesDeposited', value)}
            // description="Yes (≤1900cc) reduces value by 5%, Yes (>1900cc) by 10%"
          />
        </div>
      </div>

      {/* Additional Notes */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Additional Information</h3>
        <Input
          label="Additional Notes"
          type="text"
          placeholder="Describe any other issues or special features..."
          value={formData.additionalNotes || ''}
          onChange={(e) => onFormChange('additionalNotes', e.target.value)}
          description="Optional: Any other details about your vehicle's condition"
        />
      </div>
    </div>
  );
};

export default VehicleConditionAssessment;